export type TimePeriod = 'AM' | 'PM';

const roundUpToNext15Minutes = (date: Date): Date => {
    const roundedDate = new Date(date);
    const minutes = roundedDate.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 15) * 15;

    if (roundedMinutes === 60) {
        roundedDate.setHours(roundedDate.getHours() + 1);
        roundedDate.setMinutes(0);
    } else {
        roundedDate.setMinutes(roundedMinutes);
    }

    roundedDate.setSeconds(0);
    roundedDate.setMilliseconds(0);

    return roundedDate;
};

const formatTimeString = (date: Date): string => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
};

const isSameCalendarDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

/**
 * The backend rejects meeting proposal start times in the past. When the selected meeting date
 * is today, returns the earliest selectable "HH:mm" start time (now rounded up to the next
 * 15-minute slot). Returns undefined when the selected date is not today, meaning no restriction
 * applies (full day is selectable, as before).
 */
export const getMinSelectableStartTime = (
    selectedDate: Date | null | undefined,
    now: Date = new Date()
): string | undefined => {
    if (!selectedDate || !isSameCalendarDay(selectedDate, now)) {
        return undefined;
    }
    return formatTimeString(roundUpToNext15Minutes(now));
};

const to24Hour = (hour12: string, period: TimePeriod): number => {
    const hour = parseInt(hour12, 10) % 12;
    return period === 'PM' ? hour + 12 : hour;
};

const toMinutesOfDay = (hour24: number, minute: string): number =>
    hour24 * 60 + (parseInt(minute, 10) || 0);

const parseHHmmToMinutesOfDay = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return (hours || 0) * 60 + (minutes || 0);
};

/**
 * Whether picking `hour12`/`minute`/`period` — combined with the other two already-selected
 * values — would produce a time earlier than `minTime` ("HH:mm", 24-hour). Used to disable
 * individual hour/minute/period options in the custom time picker so a past start time can't be
 * chosen. Returns false when `minTime` isn't set (no restriction) or an input is incomplete.
 */
export const isTimeOptionBeforeMin = (
    hour12: string,
    minute: string,
    period: TimePeriod,
    minTime?: string
): boolean => {
    if (!minTime || !hour12 || !minute) {
        return false;
    }
    return (
        toMinutesOfDay(to24Hour(hour12, period), minute) <
        parseHHmmToMinutesOfDay(minTime)
    );
};
